import React from 'react';
import { DocumentItem } from '../../types/cabinet.types';
import { useI18n } from '../../hooks/useI18n';

interface PrescriptionModalProps {
  document: DocumentItem | null;
  isOpen: boolean;
  onClose: () => void;
}

export const PrescriptionModal: React.FC<PrescriptionModalProps> = ({
  document,
  isOpen,
  onClose,
}) => {
  const { t, lang } = useI18n();

  if (!isOpen || !document || !document.drugs) return null;

  // Отримуємо Bearer token для побудови URL print сторінки
  const token = sessionStorage.getItem('cab_token') || '';

  const handleOpenPrint = () => {
    const rxId = document.id.toString().replace('rx_', '');
    const apiBase = (window as any).tb24ApiSettings?.root || '/wp-json/';
    const printUrl = `${apiBase}api_front/v1/users/me/prescriptions/${rxId}/print`;
    const url = printUrl + '?token=' + encodeURIComponent(token) + '&lang=' + encodeURIComponent(lang);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="cab-rec-overlay" onClick={onClose}>
      <div className="cab-rec-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="cab-rec-header">
          <div>
            <h2 className="cab-rec-title">{t('doc_prescription_title')} </h2>
            <div className="cab-rec-code">
              {document.ereceptaCode && (
                <>
                  <span className="cab-rec-code-label">{t('doc_prescription_code')}:</span>{' '}
                  <span className="cab-rec-code-value">{document.ereceptaCode}</span>
                </>
              )}
            </div>
            {document.date && (
              <p className="cab-rec-date">{document.date}</p>
            )}
          </div>
          <button className="cab-rec-close" onClick={onClose} aria-label={t('doc_close')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="cab-rec-body">
          <div className="cab-rec-pickup-banner">
            <div className="cab-rec-pickup-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#361777" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="8" y1="13" x2="16" y2="13"></line>
                <line x1="8" y1="17" x2="16" y2="17"></line>
                <line x1="8" y1="9" x2="11" y2="9"></line>
              </svg>
            </div>
            <div className="cab-rec-pickup-content">
              <h4>{t('doc_pickup_title')}</h4>
              <p>
                {t('doc_pickup_desc_1')}
                <strong>{t('doc_pickup_desc_2')}</strong>
                {t('doc_pickup_desc_3')}
                <strong>{t('doc_pickup_desc_4')}</strong>
              </p>
            </div>
          </div>

          <div className="cab-rx-list">
            
            {document.drugs.map((drug, idx) => (
              <div key={idx} className="cab-rx-card">
                <div className="cab-rx-card-top">
                  <span className="cab-rx-name">{drug.name}</span>
                  {drug.refundType && (
                    <span className="cab-rx-refund">| {drug.refundType}</span>
                  )}
                </div>
                <div className="cab-rx-card-bottom">
                  D.S. {drug.usage} op. {drug.boxAmount}
                </div>
              </div>
            ))}
          </div>
        </div>

        
      </div>

      <style>{`
        .cab-rec-overlay {
          position: fixed;
          inset: 0;
          background: rgba(30, 10, 60, 0.5);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: flex-end;
          justify-content: center;
          z-index: 9999;
          padding: 0 16px;
          animation: cab-rec-fade-in 0.2s ease;
        }
        @keyframes cab-rec-fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .cab-rec-modal {
          background: #fff;
          border-radius: 24px 24px 0 0;
          width: 100%;
          max-width: 680px;
          max-height: 90dvh;
          display: flex;
          flex-direction: column;
          animation: cab-rec-slide-up 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
          box-shadow: 0 -10px 60px rgba(54, 23, 119, 0.15);
        }
        @keyframes cab-rec-slide-up {
          from { transform: translateY(60px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @media (min-width: 600px) {
          .cab-rec-overlay {
            align-items: center;
            padding: 20px;
          }
          .cab-rec-modal {
            border-radius: 24px;
            max-height: 85vh;
          }
        }
        .cab-rec-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 24px 20px 16px 20px;
          background: #fff;
          border-radius: 24px 24px 0 0;
          flex-shrink: 0;
        }
        .cab-rec-title {
          font-family: 'Poppins', sans-serif;
          font-size: 24px;
          font-weight: 600;
          color: var(--cab-violet, #361777);
          margin: 0 0 4px;
          line-height: 1.2;
        }
        .cab-rec-code {
          font-family: 'Poppins', sans-serif;
          font-size: 16px;
          color: #786798;
          margin: 0 0 4px;
        }
        .cab-rec-code-label {
          font-weight: 600;
        }
        .cab-rec-code-value {
          font-weight: 600;
        }
        .cab-rec-date {
          font-family: 'Poppins', sans-serif;
          font-size: 14px;
          color: var(--cab-note, #786798);
          margin: 0;
        }
        .cab-rec-close {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          border: none;
          background: #F4F0FA;
          color: var(--cab-violet, #361777);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          flex-shrink: 0;
          transition: background 0.2s;
        }
        .cab-rec-close svg {
          width: 20px;
          height: 20px;
        }
        .cab-rec-close:hover { background: #E6D6FA; }
        .cab-rec-body {
          flex: 1;
          overflow-y: auto;
          padding: 0 20px 24px 20px;
          overscroll-behavior: contain;
        }
        
        .cab-rec-pickup-banner {
          background: #E8DFF2;
          border-radius: 20px;
          padding: 16px 16px;
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 24px;
        }
        .cab-rec-pickup-icon {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .cab-rec-pickup-icon svg {
          width: 24px;
          height: 24px;
        }
        .cab-rec-pickup-content h4 {
          font-family: 'Poppins', sans-serif;
          font-size: 15px;
          font-weight: 600;
          color: var(--cab-violet, #361777);
          margin: 0 0 2px;
        }
        .cab-rec-pickup-content p {
          font-family: 'Poppins', sans-serif;
          font-size: 13px;
          color: var(--cab-note, #786798);
          margin: 0;
          line-height: 1.4;
        }
        .cab-rec-pickup-content p strong {
          font-weight: 600;
          color: var(--cab-note, #786798);
        }
        
        .cab-rx-card {
          background: #F4F4F4;
          border-radius: 16px;
          padding: 16px;
          margin-bottom: 12px;
        }
        .cab-rx-card-top {
          font-family: 'Poppins', sans-serif;
          font-size: 15px;
          color: #786798;
          margin-bottom: 4px;
        }
        .cab-rx-card-top:last-child {
          margin-bottom: 0;
        }
        .cab-rx-name {
          font-weight: 500;
          color: #786798;
        }
        .cab-rx-refund {
          color: var(--cab-note, #786798);
          margin-left: 4px;
        }
        .cab-rx-card-bottom {
          font-family: 'Poppins', sans-serif;
          font-size: 14px;
          color: var(--cab-note, #786798);
        }

        @media (min-width: 600px) {
          .cab-rec-header {
            padding: 32px 45px 16px 45px;
          }
          .cab-rec-title {
            font-size: 24px;
          }
          .cab-rec-code {
            font-size: 16px;
            color: #786798;
          }
          .cab-rec-close {
            width: 44px;
            height: 44px;
          }
          .cab-rec-close svg {
            width: 24px;
            height: 24px;
          }
          .cab-rec-body {
            padding: 0 45px 32px 45px;
          }
          .cab-rec-pickup-banner {
            border-radius: 32px;
            padding: 20px 16px;
            gap: 20px;
          }
          .cab-rec-pickup-icon {
            width: 56px;
            height: 56px;
          }
          .cab-rec-pickup-icon svg {
            width: 26px;
            height: 26px;
          }
          .cab-rec-pickup-content h4 {
            font-size: 18px;
          }
          .cab-rec-pickup-content p {
            font-size: 15px;
          }
          .cab-rx-card {
            padding: 20px 16px;
          }
          .cab-rx-card-top {
            font-size: 16px;
          }
          .cab-rx-card-bottom {
            font-size: 15px;
          }
        }

        .cab-rec-footer {
          background: #fff;
          padding: 16px 20px 24px;
          border-top: 1px solid #E6D6FA;
          border-radius: 0 0 24px 24px;
          flex-shrink: 0;
        }
        .cab-rec-print-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          height: 48px;
          border-radius: 100px;
          border: 2px solid var(--cab-violet, #361777);
          background: transparent;
          color: var(--cab-violet, #361777);
          font-family: 'Poppins', sans-serif;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s, color 0.2s;
        }
        .cab-rec-print-btn:hover {
          background: var(--cab-violet, #361777);
          color: #fff;
        }
      `}</style>
    </div>
  );
};
